import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';

import { db } from '@/config/firebase';
import { Collaborator } from '@/models';

export type BenefitsBreakdown = {
  mealAllowance?: number | null;
  healthPlan?: number | null;
  healthPromotion?: number | null;
  foodBenefit?: number | null;
  funeralAssistance?: number | null;
  petPlan?: number | null;
  financialEducation?: number | null;
  transportation?: number | null;
  total?: number | null;
  subtotal?: number | null;
};

export type PayrollSummary = {
  earningsTotal?: number | null;
  deductionsTotal?: number | null;
  net?: number | null;
  baseSalary?: number | null;
  fgts?: number | null;
  encumbrancesSubtotal?: number | null;
};

export type ProvisionsSummary = {
  thirteenth?: number | null;
  vacation?: number | null;
  constitutional?: number | null;
  fgtsVacation?: number | null;
  fgtsThirteenth?: number | null;
  fgtsPenalty?: number | null;
  total?: number | null;
};

export type ComputedExpenses = {
  charges?: number | null;
  encumbrancesSubtotal?: number | null;
  provisions?: ProvisionsSummary | null;
  provisionsTotal?: number | null;
  total?: number | null;
  hourlyCost?: number | null;
};

export type CalculatorTotals = {
  benefitsSubtotal?: number | null;
  encumbrancesSubtotal?: number | null;
  provisionsTotal?: number | null;
  total?: number | null;
  hourlyCost?: number | null;
  salary?: number | null;
};

export type CalculatorBreakdown = {
  benefitsCost?: number | null;
  laborObligationsCost?: number | null;
  monthlyCost?: number | null;
  hourlyCost?: number | null;
  salaryPerHour?: number | null;
  taxes?: number | null;
  benefitsBreakdown?: BenefitsBreakdown | null;
  laborObligationsBreakdown?: {
    profitSharing?: number | null;
    inss?: number | null;
    fgts?: number | null;
    vacation?: number | null;
    thirteenth?: number | null;
    notice?: number | null;
    sickLeave?: number | null;
    paternityLeave?: number | null;
    terminationFine?: number | null;
  } | null;
  input?: { salary?: number | null };
};

export type AveragesSnapshot = {
  collaboratorCount?: number | null;
  benefits?: BenefitsBreakdown;
  payroll?: {
    baseSalary?: number | null;
    fgts?: number | null;
    charges?: number | null;
    provents?: number | null;
    encumbrancesSubtotal?: number | null;
    hourlyCost?: number | null;
  };
  provisions?: ProvisionsSummary;
  totals?: {
    total?: number | null;
    hourlyCost?: number | null;
    encumbrancesSubtotal?: number | null;
    benefitsSubtotal?: number | null;
  };
};

export type ExpensesSummary = {
  competence?: string;
  total?: number | null;
  grandTotal?: number | null;
  benefits?: BenefitsBreakdown;
  benefitsBreakdown?: BenefitsBreakdown | null;
  averages?: AveragesSnapshot | null;
  payroll?: PayrollSummary | null;
  calculator?: CalculatorBreakdown | null;
  calculatorTotals?: CalculatorTotals | null;
  computed?: ComputedExpenses | null;
  reimbursements?: number | null;
};

export type ExpensesView = {
  baseSalaryValue?: number;
  fgtsValue?: number;
  chargesValue?: number;
  averagesBenefits: BenefitsBreakdown;
  averagesPayroll: NonNullable<AveragesSnapshot['payroll']>;
  averagesProvisions: NonNullable<AveragesSnapshot['provisions']>;
  averagesTotals: NonNullable<AveragesSnapshot['totals']>;
  provisions: ProvisionsSummary;
  provisionsTotal?: number | null;
  calcBenefitsBreakdown: BenefitsBreakdown | null;
  calcLaborBreakdown: CalculatorBreakdown['laborObligationsBreakdown'];
  calcSalary?: number | null;
  calcBenefitsSubtotal?: number | null;
  calcProvisions: ProvisionsSummary;
  calcProvisionsTotal?: number | null;
  calcEncargosSubtotal?: number | null;
  expensesEncargosSubtotal?: number | null;
  expensesBenefitsSubtotal?: number | null;
  calcBenefitsDisplay?: number | null;
  calcTotal?: number | null;
  expensesTotal?: number | null;
  avgBase?: number | null;
  avgFgts?: number | null;
  avgCharges?: number | null;
  avgProvisions: ProvisionsSummary;
  avgProvisionsTotal?: number | null;
  avgEncumbrancesSubtotal?: number | null;
  avgBenefitsSubtotal?: number | null;
  avgTotal?: number | null;
  avgHourlyCost?: number | null;
  hourlyCost?: number | null;
};

const toNumberOrNull = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : null;
  return Number.isFinite(parsed as number) ? (parsed as number) : null;
};

const emptyBenefits: BenefitsBreakdown = {};
const emptyPayroll: NonNullable<AveragesSnapshot['payroll']> = {};
const emptyProvisions: ProvisionsSummary = {};
const emptyTotals: NonNullable<AveragesSnapshot['totals']> = {};

export function useCollaboratorExpensesSummary(collaborator?: Collaborator | null) {
  const [expensesSummary, setExpensesSummary] = useState<ExpensesSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchExpenses = async () => {
      if (!collaborator?.id) return;
      const pathFromProfile = (collaborator as any).lastExpensesPath as string | undefined;
      const competenceKey =
        (collaborator as any).lastExpensesCompetence || (collaborator as any).lastCompetence;
      const docPath =
        pathFromProfile || (competenceKey ? `expenses/${competenceKey}/collaborators/${collaborator.id}` : null);
      if (!docPath) return;
      setIsLoading(true);
      try {
        const docRef = doc(db, docPath);
        const snap = await getDoc(docRef);
        if (!snap.exists()) return;
        const data = snap.data() as any;
        const pathParts = docPath.split('/');
        const competenceFromPath = pathParts.length >= 2 ? pathParts[1] : undefined;
        const competenceResolved = data.competence || competenceFromPath || competenceKey || undefined;

        const normalizeBenefits = (payload?: any): BenefitsBreakdown => {
          if (typeof payload === 'number') {
            return { total: toNumberOrNull(payload) };
          }
          return {
            mealAllowance: toNumberOrNull(payload?.mealAllowance),
            healthPlan: toNumberOrNull(payload?.healthPlan),
            healthPromotion: toNumberOrNull(payload?.healthPromotion),
            foodBenefit: toNumberOrNull(payload?.foodBenefit),
            funeralAssistance: toNumberOrNull(payload?.funeralAssistance),
            petPlan: toNumberOrNull(payload?.petPlan),
            financialEducation: toNumberOrNull(payload?.financialEducation),
            transportation: toNumberOrNull(payload?.transportation),
            total: toNumberOrNull(payload?.total ?? payload?.subtotal),
            subtotal: toNumberOrNull(payload?.subtotal ?? payload?.total),
          };
        };

        const normalizeProvisions = (payload?: any): ProvisionsSummary | null => {
          if (!payload) return null;
          return {
            thirteenth: toNumberOrNull(payload.thirteenth),
            vacation: toNumberOrNull(payload.vacation),
            constitutional: toNumberOrNull(payload.constitutional),
            fgtsVacation: toNumberOrNull(payload.fgtsVacation),
            fgtsThirteenth: toNumberOrNull(payload.fgtsThirteenth),
            fgtsPenalty: toNumberOrNull(payload.fgtsPenalty),
            total: toNumberOrNull(payload.total),
          };
        };

        const normalizeAverages = (raw?: any): AveragesSnapshot | null => {
          if (!raw) return null;
          return {
            collaboratorCount: toNumberOrNull(raw.collaboratorCount),
            benefits: raw.benefits
              ? {
                  healthPlan: toNumberOrNull(raw.benefits.healthPlan),
                  healthPromotion: toNumberOrNull(raw.benefits.healthPromotion),
                  foodBenefit: toNumberOrNull(raw.benefits.foodBenefit),
                  funeralAssistance: toNumberOrNull(raw.benefits.funeralAssistance),
                  petPlan: toNumberOrNull(raw.benefits.petPlan),
                  financialEducation: toNumberOrNull(raw.benefits.financialEducation),
                  transportation: toNumberOrNull(raw.benefits.transportation),
                  total: toNumberOrNull(raw.benefits.total ?? raw.benefits.subtotal),
                  subtotal: toNumberOrNull(raw.benefits.subtotal ?? raw.benefits.total),
                }
              : undefined,
            payroll: raw.payroll
              ? {
                  baseSalary: toNumberOrNull(raw.payroll.baseSalary),
                  fgts: toNumberOrNull(raw.payroll.fgts),
                  charges: toNumberOrNull(raw.payroll.charges),
                  provents: toNumberOrNull(raw.payroll.provents),
                  encumbrancesSubtotal: toNumberOrNull(raw.payroll.encumbrancesSubtotal),
                  hourlyCost: toNumberOrNull(raw.payroll.hourlyCost),
                }
              : undefined,
            provisions: raw.provisions
              ? {
                  thirteenth: toNumberOrNull(raw.provisions.thirteenth),
                  vacation: toNumberOrNull(raw.provisions.vacation),
                  constitutional: toNumberOrNull(raw.provisions.constitutional),
                  fgtsVacation: toNumberOrNull(raw.provisions.fgtsVacation),
                  fgtsThirteenth: toNumberOrNull(raw.provisions.fgtsThirteenth),
                  fgtsPenalty: toNumberOrNull(raw.provisions.fgtsPenalty),
                  total: toNumberOrNull(raw.provisions.total),
                }
              : undefined,
            totals: raw.totals
              ? {
                  total: toNumberOrNull(raw.totals.total),
                  hourlyCost: toNumberOrNull(raw.totals.hourlyCost),
                  encumbrancesSubtotal: toNumberOrNull(raw.totals.encumbrancesSubtotal ?? raw.payroll?.encumbrancesSubtotal),
                  benefitsSubtotal: toNumberOrNull(raw.totals.benefitsSubtotal ?? raw.benefits?.total),
                }
              : undefined,
          };
        };

        if (!cancelled) {
          setExpensesSummary({
            competence: competenceResolved,
            total: toNumberOrNull(data.total ?? data.grandTotal ?? data.payroll?.net),
            grandTotal: toNumberOrNull(data.grandTotal),
            reimbursements: toNumberOrNull(data.reimbursements),
            benefits: normalizeBenefits(data.benefits ?? data.benefitsBreakdown ?? {}),
            benefitsBreakdown: (data.benefitsBreakdown || data.benefits)
              ? normalizeBenefits(data.benefitsBreakdown ?? data.benefits ?? {})
              : null,
            averages: normalizeAverages(data.averages ?? data.averagesSnapshot ?? data?.totals?.averages),
            payroll: data.payroll
              ? {
                  net: toNumberOrNull(data.payroll.net ?? data.payroll.netPay),
                  baseSalary: toNumberOrNull(data.payroll.baseSalary ?? data.payroll.payrollSalary ?? data.payrollSalary),
                  fgts: toNumberOrNull(data.payroll.fgtsValue ?? data.payroll.fgts ?? data.payroll.fgtsAmount),
                  earningsTotal: toNumberOrNull(
                    data.payroll.earningsTotal ?? (data as any).earningsTotal ?? (data.payroll as any)?.earningsTotal,
                  ),
                  deductionsTotal: toNumberOrNull(data.payroll.deductionsTotal ?? (data as any).deductionsTotal),
                  encumbrancesSubtotal: toNumberOrNull(data.payroll.encumbrancesSubtotal),
                }
              : null,
            calculator: data.calculator
              ? {
                  benefitsCost: toNumberOrNull(data.calculator.result?.benefitsCost ?? data.calculator.benefitsCost),
                  laborObligationsCost: toNumberOrNull(
                    data.calculator.result?.laborObligationsCost ?? data.calculator.laborObligationsCost,
                  ),
                  monthlyCost: toNumberOrNull(data.calculator.result?.monthlyCost ?? data.calculator.monthlyCost),
                  hourlyCost: toNumberOrNull(
                    data.calculator.result?.hourlyCost ??
                      (data as any).calculator?.hourlyCost ??
                      (data as any).calculator?.result?.hourlyCost,
                  ),
                  salaryPerHour: toNumberOrNull(data.calculator.result?.salaryPerHour ?? data.calculator.salaryPerHour),
                  taxes: toNumberOrNull(data.calculator.result?.taxes ?? data.calculator.taxes),
                  benefitsBreakdown: data.calculator.result?.benefitsBreakdown ?? data.calculator.benefitsBreakdown ?? null,
                  laborObligationsBreakdown:
                    data.calculator.result?.laborObligationsBreakdown ??
                    data.calculator.laborObligationsBreakdown ??
                    null,
                  input: {
                    salary: toNumberOrNull(data.calculator.input?.salary ?? data.payroll?.baseSalary ?? data.payrollSalary),
                  },
                }
              : null,
            calculatorTotals: data.calculatorTotals
              ? {
                  benefitsSubtotal: toNumberOrNull(data.calculatorTotals.benefitsSubtotal),
                  encumbrancesSubtotal: toNumberOrNull(data.calculatorTotals.encumbrancesSubtotal),
                  provisionsTotal: toNumberOrNull(data.calculatorTotals.provisionsTotal),
                  total: toNumberOrNull(data.calculatorTotals.total),
                  hourlyCost: toNumberOrNull(data.calculatorTotals.hourlyCost),
                  salary: toNumberOrNull(data.calculatorTotals.salary),
                }
              : null,
            computed: data.computed
              ? {
                  charges: toNumberOrNull(data.computed.charges),
                  encumbrancesSubtotal: toNumberOrNull(data.computed.encumbrancesSubtotal),
                  provisions: normalizeProvisions(data.computed.provisions),
                  provisionsTotal: toNumberOrNull(data.computed.provisionsTotal),
                  total: toNumberOrNull(data.computed.total ?? data.total),
                  hourlyCost: toNumberOrNull(data.computed.hourlyCost),
                }
              : null,
          });
        }
      } catch (err) {
        console.error('[useCollaboratorExpensesSummary] Failed to fetch expenses summary', err);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    fetchExpenses();
    return () => {
      cancelled = true;
    };
  }, [collaborator?.id, (collaborator as any)?.lastExpensesPath, (collaborator as any)?.lastExpensesCompetence, (collaborator as any)?.lastCompetence]);

  return { expensesSummary, isLoading };
}

export function buildExpensesView(expensesSummary: ExpensesSummary | null): ExpensesView {
  const baseSalaryValue = expensesSummary?.payroll?.baseSalary ?? undefined;
  const fgtsValue = expensesSummary?.payroll?.fgts ?? undefined;
  const chargesValue = expensesSummary?.computed?.charges ?? undefined;
  const averagesBenefits = expensesSummary?.averages?.benefits ?? emptyBenefits;
  const averagesPayroll = expensesSummary?.averages?.payroll ?? emptyPayroll;
  const averagesProvisions = expensesSummary?.averages?.provisions ?? emptyProvisions;
  const averagesTotals = expensesSummary?.averages?.totals ?? emptyTotals;
  const provisions = expensesSummary?.computed?.provisions ?? {};
  const provisionsTotal = expensesSummary?.computed?.provisionsTotal ?? undefined;
  const calcBenefitsBreakdown = expensesSummary?.calculator?.benefitsBreakdown ?? null;
  const calcLaborBreakdown = expensesSummary?.calculator?.laborObligationsBreakdown ?? null;
  const calcSalary =
    expensesSummary?.calculatorTotals?.salary ??
    expensesSummary?.calculator?.input?.salary ??
    baseSalaryValue;
  const calcBenefitsSubtotal =
    expensesSummary?.calculatorTotals?.benefitsSubtotal ?? expensesSummary?.calculator?.benefitsCost ?? undefined;
  const calcProvisions: ProvisionsSummary = {
    thirteenth: calcLaborBreakdown?.thirteenth ?? undefined,
    vacation: calcLaborBreakdown?.vacation ?? undefined,
    constitutional: undefined,
    fgtsVacation: undefined,
    fgtsThirteenth: undefined,
    fgtsPenalty: calcLaborBreakdown?.terminationFine ?? undefined,
  };
  const calcProvisionsTotal = expensesSummary?.calculatorTotals?.provisionsTotal ?? undefined;
  const calcEncargosSubtotal = expensesSummary?.calculatorTotals?.encumbrancesSubtotal ?? undefined;
  const expensesEncargosSubtotal = expensesSummary?.computed?.encumbrancesSubtotal ?? undefined;
  const expensesBenefitsSubtotal =
    expensesSummary?.benefits?.total ?? expensesSummary?.benefitsBreakdown?.total ?? undefined;
  const calcBenefitsDisplay = calcBenefitsSubtotal;
  const calcTotal = expensesSummary?.calculatorTotals?.total ?? undefined;
  const expensesTotal = expensesSummary?.computed?.total ?? expensesSummary?.total ?? undefined;
  const avgBase = averagesPayroll.baseSalary ?? undefined;
  const avgFgts = averagesPayroll.fgts ?? undefined;
  const avgCharges = averagesPayroll.charges ?? undefined;
  const avgProvisions: ProvisionsSummary = {
    thirteenth: averagesProvisions.thirteenth ?? undefined,
    vacation: averagesProvisions.vacation ?? undefined,
    constitutional: averagesProvisions.constitutional ?? undefined,
    fgtsVacation: averagesProvisions.fgtsVacation ?? undefined,
    fgtsThirteenth: averagesProvisions.fgtsThirteenth ?? undefined,
    fgtsPenalty: averagesProvisions.fgtsPenalty ?? undefined,
  };
  const avgProvisionsTotal = averagesProvisions.total ?? undefined;
  const avgEncumbrancesSubtotal =
    averagesTotals.encumbrancesSubtotal ?? averagesPayroll.encumbrancesSubtotal ?? undefined;
  const avgBenefitsSubtotal =
    averagesTotals.benefitsSubtotal ?? averagesBenefits.total ?? averagesBenefits.subtotal ?? undefined;
  const avgTotal = averagesTotals.total ?? undefined;
  const avgHourlyCost = averagesTotals.hourlyCost ?? averagesPayroll.hourlyCost ?? undefined;
  const hourlyCost = expensesSummary?.computed?.hourlyCost ?? undefined;

  return {
    baseSalaryValue,
    fgtsValue,
    chargesValue,
    averagesBenefits,
    averagesPayroll,
    averagesProvisions,
    averagesTotals,
    provisions,
    provisionsTotal,
    calcBenefitsBreakdown,
    calcLaborBreakdown,
    calcSalary,
    calcBenefitsSubtotal,
    calcProvisions,
    calcProvisionsTotal,
    calcEncargosSubtotal,
    expensesEncargosSubtotal,
    expensesBenefitsSubtotal,
    calcBenefitsDisplay,
    calcTotal,
    expensesTotal,
    avgBase,
    avgFgts,
    avgCharges,
    avgProvisions,
    avgProvisionsTotal,
    avgEncumbrancesSubtotal,
    avgBenefitsSubtotal,
    avgTotal,
    avgHourlyCost,
    hourlyCost,
  };
}
